import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Sparkles, Gem, DollarSign, Activity, CreditCard, BarChart2, Settings } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import AdminCard from '../../components/admin/AdminCard';
import { dashboardAPI, analyticsAPI } from '../../services/api';
import '../../styles/pages/AdminDashboard.css';
import '../../styles/admin-shared.css';

const DashboardPage = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [platformStats, setPlatformStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [overviewRes, activityRes, statsRes] = await Promise.all([
        dashboardAPI.getOverview(),
        dashboardAPI.getRecentActivity(10),
        analyticsAPI.getPlatformStats(30),
      ]);

      setOverview(overviewRes.data.data);
      setRecentActivity(activityRes.data.data);
      setPlatformStats(statsRes.data.data);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Carregando dashboard...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="error-container">
          <p>{error}</p>
          <button onClick={fetchDashboardData} className="btn btn--primary">
            Tentar novamente
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="dashboard-page">
        <div className="page-header">
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Visão geral da plataforma</p>
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <AdminCard
            title="Usuários Totais"
            value={formatNumber(overview?.users?.total || 0)}
            subtitle={`${overview?.users?.active || 0} ativos (${overview?.users?.activeRate || 0}%)`}
            icon={<Users size={24} />}
            color="blue"
          />
          <AdminCard
            title="Novos Usuários (Mês)"
            value={formatNumber(overview?.users?.newThisMonth || 0)}
            subtitle="Crescimento mensal"
            icon={<Sparkles size={24} />}
            color="green"
            trend={overview?.users?.growth}
          />
          <AdminCard
            title="Assinaturas Ativas"
            value={formatNumber(overview?.subscriptions?.active || 0)}
            subtitle={`+${overview?.subscriptions?.growth || 0}% de crescimento`}
            icon={<Gem size={24} />}
            color="purple"
            trend={overview?.subscriptions?.growth}
          />
          <AdminCard
            title="Receita (Mês)"
            value={formatCurrency(overview?.revenue?.month || 0)}
            subtitle={`Hoje: ${formatCurrency(overview?.revenue?.today || 0)}`}
            icon={<DollarSign size={24} />}
            color="yellow"
          />
        </div>

        {/* Charts Section */}
        <div className="charts-section">
          <div className="chart-card">
            <h2 className="chart-title">Estatísticas da Plataforma (30 dias)</h2>
            <div className="metrics-grid">
              <div className="metric">
                <span className="metric-label">Total de Mensagens</span>
                <span className="metric-value">
                  {formatNumber(platformStats?.summary?.total_messages || 0)}
                </span>
              </div>
              <div className="metric">
                <span className="metric-label">Posts do Fórum</span>
                <span className="metric-value">
                  {formatNumber(platformStats?.summary?.total_posts || 0)}
                </span>
              </div>
              <div className="metric">
                <span className="metric-label">Entradas de Diário</span>
                <span className="metric-value">
                  {formatNumber(platformStats?.summary?.total_entries || 0)}
                </span>
              </div>
              <div className="metric">
                <span className="metric-label">Tempo Médio (min)</span>
                <span className="metric-value">
                  {platformStats?.summary?.avg_session_time || 0}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="activity-section">
          <h2 className="section-title">Atividade Recente</h2>
          <div className="activity-list">
            {recentActivity.length === 0 ? (
              <p className="empty-state">Nenhuma atividade recente</p>
            ) : (
              recentActivity.map((activity) => (
                <div key={activity.id} className="activity-item">
                  <div className="activity-icon">
                    <Activity size={20} />
                  </div>
                  <div className="activity-content">
                    <p className="activity-text">
                      <strong>{activity.users?.name || 'Admin'}</strong> - {activity.action}
                    </p>
                    <span className="activity-time">
                      {new Date(activity.created_at).toLocaleString('pt-BR')}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="quick-actions">
          <h2 className="section-title">Ações Rápidas</h2>
          <div className="actions-grid">
            <div className="action-card" onClick={() => navigate('/admin/users')}>
              <div className="action-icon"><Users size={24} /></div>
              <h3>Gerenciar Usuários</h3>
              <p>Visualizar, editar e gerenciar usuários</p>
            </div>

            <div className="action-card" onClick={() => navigate('/admin/subscriptions')}>
              <div className="action-icon"><CreditCard size={24} /></div>
              <h3>Assinaturas</h3>
              <p>Gerenciar planos e assinaturas</p>
            </div>

            <div className="action-card" onClick={() => navigate('/admin/analytics')}>
              <div className="action-icon"><BarChart2 size={24} /></div>
              <h3>Analytics</h3>
              <p>Visualizar métricas e relatórios</p>
            </div>

            <div className="action-card" onClick={() => navigate('/admin/settings')}>
              <div className="action-icon"><Settings size={24} /></div>
              <h3>Configurações</h3>
              <p>Gerenciar configurações da plataforma</p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default DashboardPage;
